import { useState } from 'react';
import { EnvelopeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import EmptyState from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useBetaAllowlist } from './useBetaAllowlist';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validateEmail = (email: string) => EMAIL_REGEX.test(email.trim());

const parseEmails = (input: string) => {
  const emails = input
    .split(/[\n,]+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0);
  return {
    valid: emails.filter(validateEmail),
    invalid: emails.filter((e) => !validateEmail(e)),
  };
};

const formatDate = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('fr-FR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BetaAllowlistPage = () => {
  const { entries, isLoading, addEmail, removeEmail } = useBetaAllowlist();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [singleEmail, setSingleEmail] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [validationError, setValidationError] = useState('');

  const reset = () => {
    setValidationError('');
    setSingleEmail('');
    setBulkEmails('');
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleAdd = () => {
    setValidationError('');
    if (mode === 'single') {
      if (!singleEmail.trim()) return setValidationError('Veuillez entrer un email');
      if (!validateEmail(singleEmail)) return setValidationError('Email invalide');
      addEmail(singleEmail.trim());
      handleClose();
    } else {
      if (!bulkEmails.trim()) return setValidationError('Veuillez entrer au moins un email');
      const { valid, invalid } = parseEmails(bulkEmails);
      if (invalid.length > 0) return setValidationError(`Emails invalides: ${invalid.join(', ')}`);
      if (valid.length === 0) return setValidationError('Aucun email valide détecté');
      addEmail([...new Set(valid)]);
      handleClose();
    }
  };

  return (
    <>
      <PageHeader
        title="Gestion de la liste bêta"
        action={<Button onClick={() => setOpen(true)}>Ajouter email(s)</Button>}
      />

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && entries.length === 0 && (
        <EmptyState
          icon={<EnvelopeIcon />}
          title="Aucun email dans la liste"
          description="Ajoutez des adresses email pour inviter des utilisateurs au programme bêta"
        />
      )}

      {!isLoading && entries.length > 0 && (
        <Card>
          <p className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
            Liste des emails ({entries.length})
          </p>
          <Table>
            <THead>
              <tr>
                <TH>Email</TH>
                <TH>Invité le</TH>
                <TH>Inscrit le</TH>
                <TH>Statut</TH>
                <TH className="text-center">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {entries.map((entry) => (
                <TR key={entry.email}>
                  <TD className="font-medium text-gray-900 dark:text-gray-100">{entry.email}</TD>
                  <TD>{formatDate(entry.invited_at)}</TD>
                  <TD>{formatDate(entry.signed_up_at)}</TD>
                  <TD>
                    {entry.signed_up_at ? (
                      <Badge color="green">Inscrit</Badge>
                    ) : (
                      <Badge color="yellow">En attente</Badge>
                    )}
                  </TD>
                  <TD className="text-center">
                    <ConfirmDialog
                      title="Retirer de la liste bêta"
                      body="Êtes-vous sûr de vouloir retirer cet email de la liste bêta ?"
                      confirm="Retirer"
                      onConfirm={() => removeEmail(entry.email)}
                      trigger={
                        <button
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-error-500/40 text-error-500 transition-colors hover:border-error-500 hover:bg-error-500/10"
                          title="Retirer de la liste"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      }
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        title="Ajouter à la liste bêta"
        footer={
          <>
            <Button variant="outline" onClick={handleClose}>
              Annuler
            </Button>
            <Button onClick={handleAdd}>Ajouter</Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            {(['single', 'bulk'] as const).map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMode(m);
                  setValidationError('');
                }}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  mode === m
                    ? 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-300'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800'
                }`}
              >
                {m === 'single' ? 'Un seul email' : 'Plusieurs emails'}
              </button>
            ))}
          </div>

          {mode === 'single' ? (
            <Input
              type="email"
              label="Email"
              placeholder="exemple@email.com"
              value={singleEmail}
              onChange={(e) => {
                setSingleEmail(e.target.value);
                setValidationError('');
              }}
            />
          ) : (
            <Textarea
              label="Emails (un par ligne)"
              rows={6}
              placeholder={`un.email@example.com\nautre.email@example.com`}
              value={bulkEmails}
              onChange={(e) => {
                setBulkEmails(e.target.value);
                setValidationError('');
              }}
            />
          )}

          {validationError && <p className="text-sm text-error-500">{validationError}</p>}
        </div>
      </Modal>
    </>
  );
};

export default BetaAllowlistPage;
